import { isDevEnv, isNil, isReadonlyArray } from '@livestore/utils'
import { Hash, Option, Schema } from '@livestore/utils/effect'

import type { SqliteDb } from './adapter-types.ts'
import type { EventDef, Materializer, MaterializerContextQuery, MaterializerResult } from './schema/EventDef/mod.ts'
import type * as LiveStoreEvent from './schema/LiveStoreEvent/mod.ts'
import type { LiveStoreSchema } from './schema/schema.ts'
import type { QueryBuilder } from './schema/state/sqlite/query-builder/api.ts'
import { isQueryBuilder } from './schema/state/sqlite/query-builder/api.ts'
import { getResultSchema } from './schema/state/sqlite/query-builder/impl.ts'
import type { BindValues } from './sql-queries/sql-queries.ts'
import type { ParamsObject, PreparedBindValues } from './util.ts'
import { prepareBindValues } from './util.ts'

export const getExecStatementsFromMaterializer = ({
  eventDef,
  materializer,
  dbState,
  event,
}: {
  eventDef: EventDef.AnyWithoutFn
  materializer: Materializer
  dbState: SqliteDb
  /** Both encoded and decoded events are supported to reduce the number of times we need to decode/encode */
  event:
    | { decoded: LiveStoreEvent.Client.Decoded; encoded: undefined }
    | { decoded: undefined; encoded: LiveStoreEvent.Client.Encoded }
}): ReadonlyArray<{
  statementSql: string
  bindValues: PreparedBindValues
  writeTables: ReadonlySet<string> | undefined
}> => {
  const eventDecoded =
    event.decoded === undefined
      ? {
          ...event.encoded,
          args: Schema.decodeUnknownSync(eventDef.schema)(event.encoded.args),
        }
      : event.decoded

  const eventArgsEncoded =
    isNil(event.decoded?.args) === true ? undefined : Schema.encodeUnknownSync(eventDef.schema)(event.decoded.args)

  const query: MaterializerContextQuery = (
    rawQueryOrQueryBuilder:
      | {
          query: string
          bindValues: ParamsObject
        }
      | QueryBuilder.Any,
  ) => {
    if (isQueryBuilder(rawQueryOrQueryBuilder) === true) {
      const { query, bindValues } = rawQueryOrQueryBuilder.asSql()
      const rawResults = dbState.select(query, prepareBindValues(bindValues, query))
      const resultSchema = getResultSchema(rawQueryOrQueryBuilder)
      return Schema.decodeSync(resultSchema)(rawResults)
    } else {
      const { query, bindValues } = rawQueryOrQueryBuilder
      return dbState.select(query, prepareBindValues(bindValues, query))
    }
  }

  const statementResults = fromMaterializerResult(
    materializer(eventDecoded.args, {
      eventDef,
      query,
      // TODO properly implement this
      currentFacts: new Map(),
      event: eventDecoded,
    }),
  )

  return statementResults.map((statementRes) => {
    const statementSql = statementRes.sql

    const bindValues = typeof statementRes === 'string' ? eventArgsEncoded : statementRes.bindValues

    const writeTables = typeof statementRes === 'string' ? undefined : statementRes.writeTables

    return { statementSql, bindValues: prepareBindValues(bindValues ?? {}, statementSql), writeTables }
  })
}

export const makeMaterializerHash =
  ({ schema, dbState }: { schema: LiveStoreSchema; dbState: SqliteDb }) =>
  (event: LiveStoreEvent.Client.Encoded): Option.Option<number> => {
    if (isDevEnv() === true) {
      // Hashing is only needed during dev-mode diagnostics. Skip work entirely for
      // unknown events (no definition/materializer) so we do not introduce noisy
      // warnings while still returning `Option.none()` to disable hash checks.
      const eventDef = schema.eventsDefsMap.get(event.name)
      const materializer = schema.state.materializers.get(event.name)
      if (eventDef === undefined || materializer === undefined) {
        return Option.none()
      }
      // For known events we replay the materializer with the encoded payload and
      // hash the resulting SQL statements. This lets us cheaply detect
      // side-effects or logic drift between leader/client materializers without
      // mutating the underlying state.
      const materializerResults = getExecStatementsFromMaterializer({
        eventDef,
        materializer,
        dbState,
        event: { decoded: undefined, encoded: event },
      })
      return Option.some(Hash.string(JSON.stringify(materializerResults)))
    }

    return Option.none()
  }

export const hashMaterializerResults = (
  materializerResults: ReadonlyArray<{
    statementSql: string
    bindValues: PreparedBindValues
    writeTables: ReadonlySet<string> | undefined
  }>,
) => Hash.string(JSON.stringify(materializerResults))

const fromMaterializerResult = (
  materializerResult: MaterializerResult | ReadonlyArray<MaterializerResult>,
): ReadonlyArray<{
  sql: string
  bindValues: BindValues
  writeTables: ReadonlySet<string> | undefined
}> => {
  if (isReadonlyArray(materializerResult) === true) {
    return materializerResult.flatMap(fromMaterializerResult)
  }
  if (isQueryBuilder(materializerResult) === true) {
    const { query, bindValues, usedTables } = materializerResult.asSql()
    return [{ sql: query, bindValues: bindValues as BindValues, writeTables: usedTables }]
  } else if (typeof materializerResult === 'string') {
    return [{ sql: materializerResult, bindValues: {} as BindValues, writeTables: undefined }]
  } else {
    return [
      {
        sql: materializerResult.sql,
        bindValues: materializerResult.bindValues,
        writeTables: materializerResult.writeTables,
      },
    ]
  }
}
